import { SelectCombobox } from 'components/combobox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAllAuditSubjectsQuery } from 'types/generated/graphql';

import type { AuditView } from '../types';

const Wrapper = styled.div`
  width: 310px;
`;

interface EventTypeSelectProps {
  selectedEventTypes: string[];
  onEventTypesChange: (eventTypes: string[]) => void;
  view: AuditView; // Which view to get subjects for (point, package, courier, contact)
}

/**
 * Reusable Event Type multi-select component
 * Used in all audit views for filtering by event types
 * Dynamically loads event subjects from API based on the view type
 */
export function EventTypeSelect({
  selectedEventTypes,
  onEventTypesChange,
  view,
}: EventTypeSelectProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useAllAuditSubjectsQuery();
  const subjects = data?.allAuditSubjects;

  // Get event types based on view, fallback to hardcoded list
  const getEventTypes = () => {
    if (!subjects) {
      return [];
    }

    switch (view) {
      case 'point':
        return subjects.point;
      case 'content':
        return subjects.package;
      case 'courier':
        return subjects.courier;
      case 'contact':
        return subjects.contact;
      default:
        return [];
    }
  };

  const eventTypes = getEventTypes().sort((a, b) =>
    t(`Events.Descriptions.${a}`).localeCompare(t(`Events.Descriptions.${b}`))
  );

  const selectedItems = eventTypes.filter((type) =>
    selectedEventTypes.includes(type)
  );

  return (
    <Wrapper>
      <SelectCombobox
        items={eventTypes}
        multiValue={selectedItems}
        onMultiChange={(values) => onEventTypesChange(values)}
        multiple
        getItemLabel={(item) => (item ? t(`Events.Descriptions.${item}`) : '')}
        getItemValue={(item) => item}
        label={t('Events.Filter.EventTypes')}
        searchPlaceholder={t('Events.Filter.SearchEventTypes')}
        emptyMessage={t('Events.Filter.NoEventTypes')}
        disabled={isLoading}
      />
    </Wrapper>
  );
}
